// dependencies
import React, { Fragment } from "react";
import { useIntl } from "react-intl";
import { StyleSheet, css } from "aphrodite";

// components
import Text from "@cloudspire/legacy-resources/src/components/Text2";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import TextInput from "@cloudspire/legacy-resources/src/components/TextInput2";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import { FieldError } from "@cloudspire/legacy-resources/src/components/Field";

// hocs
import useViewportMedia from "../../hocs/useViewportMedia";

// constants
import {
  HOTEL_SERVICE_STRATEGIES,
  HOTEL_SERVICE_STRATEGY_INTL_MESSAGES,
  HOTEL_SERVICE_STRATEGY_VALUE_FREE_QUANTITY,
} from "@cloudspire/shared/constants/hotel/service";

// utils
import stringUppercaseFirst from "@cloudspire/shared/utils/string/uppercaseFirst";
import intlFormatConstantByValue from "@cloudspire/shared/utils/intl/formatConstantByValue";

// helpers
import hotelServiceGetIntlDisplayName from "@cloudspire/shared/helpers/hotel/service/getIntlDisplayName";

type PropsType = {
  optionList: ReturnType<
    typeof import("@cloudspire/legacy-resources/src/normalizers/room/parseData").default
  >["optionList"];
  value: { id: string; booking_quantity: string }[];
  errorList: {
    [name: string]: string[];
  };
  name: string;
  shouldRenderInput?: boolean;
  onChange;
};

const styles = StyleSheet.create({
  heading: {
    color: "#3a485f",
    fontWeight: 500,
  },
  optionName: {
    fontSize: "1rem",
  },
  quantity: {
    display: "flex",
    justifyContent: "flex-end",
  },
  quantityInput: {
    maxWidth: "7.5rem",
  },
  about: {
    fontWeight: 300,
    fontSize: "0.875rem",
  },
  price: {
    fontWeight: 300,
  },
});

const RoomOptionList: React.FunctionComponent<PropsType> = (props) => {
  const {
    name,
    optionList,
    value,
    errorList,
    shouldRenderInput = true,
    onChange,
  } = props;

  const intl = useIntl();

  const { matches: responsive } = useViewportMedia({
    queries: [{ maxWidth: 901 }],
  });

  const localOptionList = optionList.reduce((newOptionList, option) => {
    const optionValue = value.find(
      (optionValue) => String(optionValue.id) === String(option.id)
    );

    newOptionList.push({
      ...option,
      booking_quantity:
        undefined !== optionValue ? optionValue.booking_quantity : "",
    });

    return newOptionList;
  }, []);

  return (
    <Row type="column" gutter={1}>
      {localOptionList.map((option, optionIndex) => {
        const hasErrorOnOption =
          (errorList?.[`options[${option.id}]`] ?? []).length > 0;

        const $price = (
          <Text aStyle={styles.price}>
            {!isNaN(option?.amount)
              ? intl.formatNumber(option.amount, {
                  style: "currency",
                  currency: "EUR",
                })
              : "n/c"}
            {HOTEL_SERVICE_STRATEGY_VALUE_FREE_QUANTITY !== option?.strategy &&
              HOTEL_SERVICE_STRATEGIES[option?.strategy] && (
                <Fragment>
                  {" "}
                  {intlFormatConstantByValue({
                    intl,
                    messages: HOTEL_SERVICE_STRATEGY_INTL_MESSAGES.byValue,
                    value: option.strategy,
                    preferredAttributes: ["label"],
                  })}
                </Fragment>
              )}
          </Text>
        );

        const optionName = stringUppercaseFirst(
          hotelServiceGetIntlDisplayName({
            hotelService: option,
            intl,
            fallback: intl.formatMessage(
              {
                defaultMessage: "Service n° {optionIndex}",
              },
              {
                optionIndex: optionIndex + 1,
              }
            ),
          })
        );

        return (
          <Col key={optionIndex}>
            <Row type="cell" gutter={0.25} maxColumns={6}>
              <Col colSpan={responsive ? 4 : 3}>
                <Text aStyle={styles.optionName}>{optionName}</Text>

                {option?.description && (
                  <Text aStyle={styles.about}>{option.description}</Text>
                )}

                {responsive && $price}

                {hasErrorOnOption && (
                  <>
                    <Stack marginTop={0.5} />

                    <FieldError
                      errorList={errorList?.[`options[${option.id}]`] ?? []}
                    />
                  </>
                )}
              </Col>

              <Col
                colSpan={2}
                {...(responsive && { style: { display: "none" } })}
              >
                {$price}
              </Col>

              {shouldRenderInput && (
                <Col colSpan={responsive ? 2 : 1}>
                  <div className={css(styles.quantity)}>
                    <TextInput
                      aStyle={styles.quantityInput}
                      min={0}
                      max={option.max_quantity}
                      style={{ width: "100%" }}
                      id={`roomOptionList${option.id}`}
                      data-gtm="booking-room-option-quantity"
                      type="number"
                      name={`options[${option.id}]`}
                      label={intl.formatMessage({
                        defaultMessage: "Quantité",
                      })}
                      value={option.booking_quantity ?? ""}
                      onChange={(event) => {
                        onChange({
                          target: {
                            name,
                            value: localOptionList.map((localOption) => ({
                              id: localOption.id,
                              booking_quantity:
                                String(localOption.id) === String(option.id)
                                  ? event.target.value
                                  : localOption.booking_quantity,
                            })),
                          },
                        });
                      }}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Col>
        );
      })}
    </Row>
  );
};

export default RoomOptionList;
